import { cn } from '@gettactic/helpers';
import { useWindowSize } from '@gettactic/helpers/src/hooks/useWindowSize';
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogFooterProps,
  DialogHeader
} from '../../../primitives/Dialog';

export interface ModalProps extends React.PropsWithChildren {
  opened: boolean;
  onClose: () => void;
  onOpenChange?: (isOpen: boolean) => void;
  title?: React.ReactNode;
  size?: string;
  closeOnOverlayClick?: boolean;
  isModal?: boolean;
}

export interface ModalComponent extends React.FC<ModalProps> {
  Body: React.FC<React.PropsWithChildren>;
  Footer: React.FC<DialogFooterProps>;
}

export const Modal: ModalComponent = ({
  children,
  opened,
  onClose,
  title,
  size,
  closeOnOverlayClick = true,
  onOpenChange,
  isModal
}) => {
  const browserWindowSize = useWindowSize();
  const browserHeight = browserWindowSize.height;

  const handleOnClose = () => {
    if (onOpenChange) onOpenChange?.(false);
    else if (onClose) onClose();
  };

  return (
    <Dialog
      open={opened}
      onOpenChange={(isOpen) => {
        if (onOpenChange) onOpenChange(isOpen);
        else if (!isOpen) onClose();
      }}
      modal={isModal}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        className={`${size ? size : 'max-w-[30rem]'}`}
        onOpenAutoFocus={(e) => e.preventDefault()}
        overlayProps={{
          onClick: closeOnOverlayClick ? handleOnClose : undefined
        }}
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          {typeof title === 'string' ? (
            <span className="font-bold text-2xl text-secondary [.tactic-v2_&]:text-secondary-foreground">
              {title}
            </span>
          ) : (
            title || null
          )}
        </DialogHeader>
        <div className={`pt-4 ${size ? size : 'max-w-[30rem]'}`}>
          <div
            style={{
              maxHeight: browserHeight ? `${browserHeight - 200}px` : '32rem'
            }}
            className={cn(
              'pr-3 pl-1',
              browserHeight && browserHeight < 700 ? 'overflow-y-auto' : ''
            )}
          >
            {children}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ModalBody: ModalComponent['Body'] = ({ children }) => {
  return <>{children}</>;
};

export const ModalFooter: ModalComponent['Footer'] = (props) => {
  return (
    <DialogFooter {...props} className={cn('mt-4 mb-1', props.className)} />
  );
};

Modal.Body = ModalBody;
Modal.Footer = ModalFooter;
export default Modal;
